import React from 'react';
import { Link } from 'react-router-dom';
import { toSupabaseRenderImageUrl } from '../../lib/imageUtils';

interface PopularLocation {
  name: string;
  count: number;
  image: string;
  description: string;
}

function locationCardImage(url: string): string {
  /** Minimal render query — same shape as Storage image API (PNG sources, location hero art). */
  return (
    toSupabaseRenderImageUrl(url, {
      width: 640,
      height: 360,
      resize: 'cover',
      minimal: true,
    }) ?? url
  );
}

const popularLocations: PopularLocation[] = [
  { 
    name: 'Manila', 
    count: 207, 
    image: 'https://fzxcsejgdughszhmmwub.supabase.co/storage/v1/object/public/property-images/locations/Manila.png',
    description: 'Capital energy, heritage districts, and strong condo and apartment stock near work and transit.'
  },
  { 
    name: 'Quezon City', 
    count: 237, 
    image: 'https://fzxcsejgdughszhmmwub.supabase.co/storage/v1/object/public/property-images/locations/QuezonCity.png',
    description: "Metro Manila's largest city—family neighborhoods, universities, and varied budgets."
  },
  { 
    name: 'Cebu', 
    count: 86, 
    image: 'https://fzxcsejgdughszhmmwub.supabase.co/storage/v1/object/public/property-images/locations/Cebu.png',
    description: 'Visayas hub with beaches nearby—popular for BPO professionals and island lifestyle.'
  },
  { 
    name: 'Davao', 
    count: 182, 
    image: 'https://fzxcsejgdughszhmmwub.supabase.co/storage/v1/object/public/property-images/locations/Davao.png',
    description: 'Mindanao’s gateway—green spaces, growing CBD, and a calmer pace than mega-metro Manila.'
  }
];

// Ensure React is available
const ReactMemo = React?.memo || ((component: any) => component);

const PopularLocationsSection = ReactMemo(() => {
  return (
    <section
      className="bg-white py-20 sm:py-24"
      aria-labelledby="popular-locations-heading"
      role="region"
    >
      <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            Locations
          </p>
          <h2
            id="popular-locations-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
          >
            Explore our most requested locations
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            Start your search in the Philippine cities our renters ask for most.
          </p>
        </header>

        <div
          className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10 lg:grid-cols-4"
          role="list"
          aria-label="Popular real estate locations in the Philippines"
        >
          {popularLocations.map((location, index) => (
            <article key={index} role="listitem">
              <Link
                to={`/properties?location=${location.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="group home-surface-interactive rounded-2xl overflow-hidden block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-describedby={`location-${index}-description`}
                aria-label={`Explore ${location.count} properties in ${location.name}`}
              >
                <div className="relative h-52 overflow-hidden sm:h-56">
                  <img 
                    src={locationCardImage(location.image)} 
                    alt={`Cityscape of ${location.name}, Philippines - showing urban landscape and development`}
                    width={640}
                    height={360}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" aria-hidden="true"></div>
                  <div className="absolute top-3 right-3">
                    <div 
                      className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold"
                      aria-label={`${location.count} available properties`}
                    >
                      {location.count} properties
                    </div>
                  </div>
                </div>
              
                <div className="p-6 sm:p-7">
                  <h3 className="mb-2 text-xl font-bold text-neutral-800 group-hover:text-primary-600">
                    {location.name}
                  </h3>
                  <p 
                    id={`location-${index}-description`}
                    className="text-sm leading-relaxed text-neutral-600"
                  >
                    {location.description}
                  </p>
                  
                  <div className="mt-4 flex items-center text-primary-600 group-hover:text-primary-700">
                    <span className="text-sm font-semibold">Explore Properties</span>
                    <svg 
                      className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>
        
        <footer className="mt-12 text-center">
          <Link 
            to="/properties" 
            className="inline-flex items-center px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 shadow-lg hover:shadow-xl transition-transform duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-describedby="view-all-locations-description"
          >
            View All Locations
            <svg 
              className="w-5 h-5 ml-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <div id="view-all-locations-description" className="sr-only">
            Browse properties in all available locations across the Philippines
          </div>
        </footer>
      </div>
    </section>
  );
});

PopularLocationsSection.displayName = 'PopularLocationsSection';

export default PopularLocationsSection;